import { useEffect, useState } from 'react';
import { ChevronDown, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { Switch } from '@headlessui/react';
import { toast } from 'sonner';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { useBusinessId } from '../hooks/useBusinessId';
import { getSiteUrl } from '../lib/siteConfig';
import SelectServiceModal from '../components/manage/SelectServiceModal';
import { Service } from '../lib/services';

type QueTab = 'intake' | 'queue' | 'handoff' | 'design';

const tabs: { id: QueTab; label: string }[] = [
  { id: 'intake', label: 'Intake' },
  { id: 'queue', label: 'Queue' },
  { id: 'handoff', label: 'Handoff' },
  { id: 'design', label: 'Design' },
];

export default function QueBuilder() {
  const [activeTab, setActiveTab] = useState<QueTab>('intake');
  const supabase = useSupabase();
  const businessId = useBusinessId();
  const { selectedBusiness } = useBusiness();
  const [categories, setCategories] = useState<{
    id: string;
    category_title: string;
    services?: Array<{ service: Service; priceOption: any }>;
  }[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [categorySelectionEnabled, setCategorySelectionEnabled] = useState(true);
  const [isLoadingCategorySetting, setIsLoadingCategorySetting] = useState(false);
  const [stripePayEnabled, setStripePayEnabled] = useState(false);
  const [isLoadingStripeSetting, setIsLoadingStripeSetting] = useState(false);
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [advanceToHandoff, setAdvanceToHandoff] = useState(false);
  const [isLoadingHandoffSetting, setIsLoadingHandoffSetting] = useState(false);
  const [handoffBookingStatus, setHandoffBookingStatus] = useState<'scheduled' | 'in_progress' | 'completed' | null>(null);
  const [handoffCompletedAction, setHandoffCompletedAction] = useState<'create_booking' | 'none' | null>(null);
  const [hideCompletedFromCalendar, setHideCompletedFromCalendar] = useState(false);
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerColor, setHeaderColor] = useState('#111827');
  const [displayLogo, setDisplayLogo] = useState(false);
  const [displayCompanyName, setDisplayCompanyName] = useState(false);
  const [categorySelectionLabel, setCategorySelectionLabel] = useState('Select Category');
  const [handoffStages, setHandoffStages] = useState<Array<{
    id: string;
    title: string;
    stage_order: number;
    max_bookings: number;
    handoff_mode: 'automatic' | 'manual';
  }>>([]);
  const [isLoadingHandoffStages, setIsLoadingHandoffStages] = useState(false);
  const [handoffTitle, setHandoffTitle] = useState('');
  const [handoffOrder, setHandoffOrder] = useState(1);
  const [handoffMaxBookings, setHandoffMaxBookings] = useState(1);
  const [handoffMode, setHandoffMode] = useState<'automatic' | 'manual'>('automatic');
  const [isSavingHandoffStage, setIsSavingHandoffStage] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [selectedCategoryForServices, setSelectedCategoryForServices] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryTitle, setEditingCategoryTitle] = useState('');
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [selectedServiceForSecondary, setSelectedServiceForSecondary] = useState<{
    categoryId: string;
    serviceId: string;
    priceOptionId: string;
  } | null>(null);
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [editingStageTitle, setEditingStageTitle] = useState('');
  const [editingStageMaxBookings, setEditingStageMaxBookings] = useState(1);
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [thankYouTitle, setThankYouTitle] = useState("You're in the Queue!");
  const [thankYouMessage, setThankYouMessage] = useState("We've added you to the queue and will get to you as soon as possible.");

  const fetchCategories = async () => {
    if (!businessId) return;
    setIsLoadingCategories(true);
    try {
      const { data, error } = await supabase
        .from('que_intake_flow')
        .select('id, category_title, services')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading queue intake categories:', error);
      toast.error('Failed to load queue categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [businessId]);

  const fetchCategorySetting = async () => {
    if (!businessId) return;
    setIsLoadingCategorySetting(true);
    try {
      const { data, error } = await supabase
        .from('que_settings')
        .select('category_selection_enabled, stripe_pay_enabled, advance_to_handoff, handoff_booking_status, handoff_completed_action, hide_completed_from_calendar, header_enabled, header_color, display_logo, display_company_name, category_selection_label, thank_you_title, thank_you_message')
        .eq('business_id', businessId)
        .maybeSingle();

      if (error) throw error;

      if (data?.category_selection_enabled === undefined || data === null) {
        setCategorySelectionEnabled(true);
        setStripePayEnabled(false);
        setAdvanceToHandoff(false);
        setHandoffBookingStatus(null);
        setHandoffCompletedAction(null);
        setHideCompletedFromCalendar(false);
        setHeaderEnabled(false);
        setHeaderColor('#111827');
        setDisplayLogo(false);
        setDisplayCompanyName(false);
        setCategorySelectionLabel('Select Category');
        setThankYouTitle("You're in the Queue!");
        setThankYouMessage("We've added you to the queue and will get to you as soon as possible.");
        return;
      }

      setCategorySelectionEnabled(Boolean(data.category_selection_enabled));
      setStripePayEnabled(Boolean(data.stripe_pay_enabled));
      setAdvanceToHandoff(Boolean(data.advance_to_handoff));
      setHandoffBookingStatus(data.handoff_booking_status || null);
      setHandoffCompletedAction(data.handoff_completed_action || null);
      setHideCompletedFromCalendar(Boolean(data.hide_completed_from_calendar));
      setHeaderEnabled(Boolean(data.header_enabled));
      setHeaderColor(data.header_color || '#111827');
      setDisplayLogo(Boolean(data.display_logo));
      setDisplayCompanyName(Boolean(data.display_company_name));
      setCategorySelectionLabel(data.category_selection_label || 'Select Category');
      setThankYouTitle(data.thank_you_title || "You're in the Queue!");
      setThankYouMessage(data.thank_you_message || "We've added you to the queue and will get to you as soon as possible.");
    } catch (error) {
      console.error('Error loading queue settings:', error);
      toast.error('Failed to load queue settings');
    } finally {
      setIsLoadingCategorySetting(false);
    }
  };

  useEffect(() => {
    fetchCategorySetting();
  }, [businessId]);

  const fetchStripeConnection = async () => {
    if (!businessId) return;
    setIsLoadingStripeSetting(true);
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, online_payments_enabled')
        .eq('id', businessId)
        .single();

      if (error) throw error;
      const connected = Boolean(
        data?.stripe_connect_account_id && data?.online_payments_enabled !== false
      );
      setIsStripeConnected(connected);
    } catch (error) {
      console.error('Error loading Stripe connection:', error);
      setIsStripeConnected(false);
    } finally {
      setIsLoadingStripeSetting(false);
    }
  };

  useEffect(() => {
    fetchStripeConnection();
  }, [businessId]);

  const fetchHandoffStages = async () => {
    if (!businessId) return;
    setIsLoadingHandoffStages(true);
    try {
      const { data, error } = await supabase
        .from('queue_handoff_stages')
        .select('id, title, stage_order, max_bookings, handoff_mode')
        .eq('business_id', businessId)
        .order('stage_order', { ascending: true });

      if (error) throw error;
      setHandoffStages((data || []).map(stage => ({
        ...stage,
        handoff_mode: stage.handoff_mode || 'automatic'
      })));
    } catch (error) {
      console.error('Error loading handoff stages:', error);
      toast.error('Failed to load handoff stages');
    } finally {
      setIsLoadingHandoffStages(false);
    }
  };

  useEffect(() => {
    fetchHandoffStages();
  }, [businessId]);

  const handleToggleCategorySelection = async (enabled: boolean) => {
    if (!businessId) return;
    setCategorySelectionEnabled(enabled);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: enabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Category selection ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating queue settings:', error);
      toast.error('Failed to update queue settings');
      setCategorySelectionEnabled(!enabled);
    }
  };

  const handleToggleStripePay = async (enabled: boolean) => {
    if (!businessId) return;
    setStripePayEnabled(enabled);

    try {
      const { data, error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: enabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        )
        .select('stripe_pay_enabled')
        .single();

      if (error) throw error;
      setStripePayEnabled(Boolean(data?.stripe_pay_enabled));
      toast.success(`Stripe Pay ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Stripe Pay setting:', error);
      toast.error(`Failed to update Stripe Pay setting${error instanceof Error ? `: ${error.message}` : ''}`);
      setStripePayEnabled(!enabled);
    }
  };

  const handleToggleAdvanceToHandoff = async (enabled: boolean) => {
    if (!businessId) return;
    setAdvanceToHandoff(enabled);
    setIsLoadingHandoffSetting(true);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: enabled,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Advance to handoff ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating handoff setting:', error);
      toast.error('Failed to update handoff setting');
      setAdvanceToHandoff(!enabled);
    } finally {
      setIsLoadingHandoffSetting(false);
    }
  };

  const handleChangeHandoffBookingStatus = async (status: 'scheduled' | 'in_progress' | 'completed' | null) => {
    if (!businessId) return;
    setHandoffBookingStatus(status);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: status,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Handoff booking status ${status ? 'set to ' + status.replace('_', ' ') : 'disabled'}`);
    } catch (error) {
      console.error('Error updating handoff booking status:', error);
      toast.error('Failed to update handoff booking status');
    }
  };

  const handleChangeHandoffCompletedAction = async (action: 'create_booking' | 'none' | null) => {
    if (!businessId) return;
    setHandoffCompletedAction(action);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: action,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Handoff completed action ${action ? 'set to ' + action.replace('_', ' ') : 'disabled'}`);
    } catch (error) {
      console.error('Error updating handoff completed action:', error);
      toast.error('Failed to update handoff completed action');
    }
  };

  const handleToggleHideCompletedFromCalendar = async (enabled: boolean) => {
    if (!businessId) return;
    setHideCompletedFromCalendar(enabled);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: enabled,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Hide completed bookings from calendar ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating hide completed setting:', error);
      toast.error('Failed to update hide completed setting');
      setHideCompletedFromCalendar(!enabled);
    }
  };

  const handleToggleHeader = async (enabled: boolean) => {
    if (!businessId) return;
    setHeaderEnabled(enabled);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: enabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Header ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating header settings:', error);
      toast.error('Failed to update header settings');
      setHeaderEnabled(!enabled);
    }
  };

  const handleHeaderColorChange = async (color: string) => {
    if (!businessId) return;
    setHeaderColor(color);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: color,
            display_logo: displayLogo,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success('Header color updated');
    } catch (error) {
      console.error('Error updating header color:', error);
      toast.error('Failed to update header color');
    }
  };

  const handleToggleDisplayLogo = async (enabled: boolean) => {
    if (!businessId) return;
    setDisplayLogo(enabled);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: enabled,
            display_company_name: displayCompanyName
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Logo ${enabled ? 'shown' : 'hidden'} in header`);
    } catch (error) {
      console.error('Error updating logo setting:', error);
      toast.error('Failed to update logo setting');
      setDisplayLogo(!enabled);
    }
  };

  const handleToggleDisplayCompanyName = async (enabled: boolean) => {
    if (!businessId) return;
    setDisplayCompanyName(enabled);

    try {
      const { error } = await supabase
        .from('que_settings')
        .upsert(
          {
            business_id: businessId,
            category_selection_enabled: categorySelectionEnabled,
            stripe_pay_enabled: stripePayEnabled,
            advance_to_handoff: advanceToHandoff,
            handoff_booking_status: handoffBookingStatus,
            handoff_completed_action: handoffCompletedAction,
            hide_completed_from_calendar: hideCompletedFromCalendar,
            header_enabled: headerEnabled,
            header_color: headerColor,
            display_logo: displayLogo,
            display_company_name: enabled
          },
          { onConflict: 'business_id' }
        );

      if (error) throw error;
      toast.success(`Company name ${enabled ? 'shown' : 'hidden'} in header`);
    } catch (error) {
      console.error('Error updating company name setting:', error);
      toast.error('Failed to update company name setting');
      setDisplayCompanyName(!enabled);
    }
  };

  const handleCreateHandoffStage = async () => {
    if (!businessId || !handoffTitle.trim()) return;
    setIsSavingHandoffStage(true);
    try {
      const { data, error } = await supabase
        .from('queue_handoff_stages')
        .insert({
          business_id: businessId,
          title: handoffTitle.trim(),
          stage_order: handoffOrder,
          max_bookings: handoffMaxBookings,
          handoff_mode: handoffMode
        })
        .select('id, title, stage_order, max_bookings, handoff_mode')
        .single();

      if (error) throw error;
      setHandoffStages(prev => [...prev, data].sort((a, b) => a.stage_order - b.stage_order));
      setHandoffTitle('');
      setHandoffOrder(handoffOrder + 1);
      setHandoffMaxBookings(1);
      setHandoffMode('automatic');
      toast.success('Handoff stage created');
    } catch (error) {
      console.error('Error creating handoff stage:', error);
      toast.error('Failed to create handoff stage');
    } finally {
      setIsSavingHandoffStage(false);
    }
  };

  const handleUpdateHandoffMode = async (stageId: string, mode: 'automatic' | 'manual') => {
    const previousStages = handoffStages;
    setHandoffStages(prev =>
      prev.map(stage => stage.id === stageId ? { ...stage, handoff_mode: mode } : stage)
    );

    try {
      const { error } = await supabase
        .from('queue_handoff_stages')
        .update({ handoff_mode: mode })
        .eq('id', stageId);

      if (error) throw error;
      toast.success('Handoff mode updated');
    } catch (error) {
      console.error('Error updating handoff mode:', error);
      toast.error('Failed to update handoff mode');
      setHandoffStages(previousStages);
    }
  };

  const handleDeleteHandoffStage = async (stageId: string) => {
    const previousStages = handoffStages;
    setHandoffStages(prev => prev.filter(stage => stage.id !== stageId));
    try {
      const { error } = await supabase
        .from('queue_handoff_stages')
        .delete()
        .eq('id', stageId);
      if (error) throw error;
      toast.success('Handoff stage deleted');
    } catch (error) {
      console.error('Error deleting handoff stage:', error);
      toast.error('Failed to delete handoff stage');
      setHandoffStages(previousStages);
    }
  };

  const handleStartEditStage = (stage: any) => {
    setEditingStageId(stage.id);
    setEditingStageTitle(stage.title);
    setEditingStageMaxBookings(stage.max_bookings);
  };

  const handleCancelEditStage = () => {
    setEditingStageId(null);
    setEditingStageTitle('');
    setEditingStageMaxBookings(1);
  };

  const handleUpdateStage = async () => {
    if (!editingStageId || !editingStageTitle.trim() || editingStageMaxBookings < 1) return;
    setIsUpdatingStage(true);
    try {
      const { error } = await supabase
        .from('queue_handoff_stages')
        .update({
          title: editingStageTitle.trim(),
          max_bookings: editingStageMaxBookings
        })
        .eq('id', editingStageId);

      if (error) throw error;

      // Update local state
      setHandoffStages(prev =>
        prev.map(stage =>
          stage.id === editingStageId
            ? { ...stage, title: editingStageTitle.trim(), max_bookings: editingStageMaxBookings }
            : stage
        )
      );
      
      toast.success('Stage updated');
      handleCancelEditStage();
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryTitle.trim() || !businessId) return;
    setIsSavingCategory(true);
    try {
      const { data, error } = await supabase
        .from('que_intake_flow')
        .insert([
          {
            business_id: businessId,
            category_title: categoryTitle.trim()
          }
        ])
        .select('id, category_title, services')
        .single();

      if (error) throw error;

      setCategories(prev => [...prev, data]);
      setCategoryTitle('');
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error creating queue category:', error);
      toast.error('Failed to create category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  const openServiceModal = (categoryId: string) => {
    setSelectedCategoryForServices(categoryId);
    setIsSelectServiceModalOpen(true);
  };

  const handleAssignService = async (service: Service, priceOption: any) => {
    if (!selectedCategoryForServices) return;
    const targetCategory = categories.find(category => category.id === selectedCategoryForServices);
    if (!targetCategory) return;

    // Check if we're adding a secondary service
    if (selectedServiceForSecondary && 
        selectedServiceForSecondary.categoryId === selectedCategoryForServices) {
      // Adding as secondary service
      const existingServices = targetCategory.services || [];
      const targetService = existingServices.find(
        item => item.service?.id === selectedServiceForSecondary.serviceId &&
        (item.priceOption?.id || item.priceOption?.price_option_id) === selectedServiceForSecondary.priceOptionId
      );

      if (!targetService) {
        toast.error('Primary service not found');
        return;
      }

      const existingSecondary = targetService.secondary_services || [];
      const alreadyAssigned = existingSecondary.some(
        (item: any) =>
          item.service?.id === service.id &&
          (item.priceOption?.id || item.priceOption?.price_option_id) ===
            (priceOption?.id || priceOption?.price_option_id)
      );

      if (alreadyAssigned) {
        toast.error('Add-on service already assigned');
        return;
      }

      const updatedSecondary = [
        ...existingSecondary,
        { service, priceOption }
      ];

      const updatedServices = existingServices.map((item: any) => {
        if (item.service?.id === selectedServiceForSecondary.serviceId &&
            (item.priceOption?.id || item.priceOption?.price_option_id) === selectedServiceForSecondary.priceOptionId) {
          return { ...item, secondary_services: updatedSecondary };
        }
        return item;
      });

      console.log('Saving secondary service. Updated services:', JSON.stringify(updatedServices, null, 2));
      
      const { error } = await supabase
        .from('que_intake_flow')
        .update({ services: updatedServices })
        .eq('id', targetCategory.id);

      if (error) {
        console.error('Error assigning secondary service:', error);
        toast.error('Failed to assign add-on service');
        return;
      }
      
      console.log('Secondary service saved successfully');

      setCategories(prev =>
        prev.map(category =>
          category.id === targetCategory.id ? { ...category, services: updatedServices } : category
        )
      );
      toast.success('Add-on service assigned');
      setIsSelectServiceModalOpen(false);
      return;
    }

    // Adding as regular service
    const existingServices = targetCategory.services || [];
    const alreadyAssigned = existingServices.some(
      item =>
        item.service?.id === service.id &&
        (item.priceOption?.id || item.priceOption?.price_option_id) ===
          (priceOption?.id || priceOption?.price_option_id)
    );

    if (alreadyAssigned) {
      toast.error('Service already assigned to this category');
      return;
    }

    const updatedServices = [
      ...existingServices,
      {
        service,
        priceOption,
        secondary_services: [] // Initialize with empty secondary services
      }
    ];

    const { error } = await supabase
      .from('que_intake_flow')
      .update({ services: updatedServices })
      .eq('id', targetCategory.id);

    if (error) {
      console.error('Error assigning service:', error);
      throw error;
    }

    setCategories(prev =>
      prev.map(category =>
        category.id === targetCategory.id ? { ...category, services: updatedServices } : category
      )
    );
    toast.success('Service assigned');
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const previousCategories = categories;
    setCategories(prev => prev.filter(category => category.id !== categoryId));

    try {
      const { error } = await supabase
        .from('que_intake_flow')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      toast.success('Category deleted');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
      setCategories(previousCategories);
    }
  };

  const handleStartEditCategory = (categoryId: string, currentTitle: string) => {
    setEditingCategoryId(categoryId);
    setEditingCategoryTitle(currentTitle);
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryTitle('');
  };

  const handleUpdateCategory = async (categoryId: string) => {
    if (!editingCategoryTitle.trim() || !businessId) return;
    setIsUpdatingCategory(true);
    
    const previousCategories = categories;
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? { ...category, category_title: editingCategoryTitle.trim() }
          : category
      )
    );

    try {
      const { error } = await supabase
        .from('que_intake_flow')
        .update({ category_title: editingCategoryTitle.trim() })
        .eq('id', categoryId);

      if (error) throw error;
      toast.success('Category title updated');
      setEditingCategoryId(null);
      setEditingCategoryTitle('');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category title');
      setCategories(previousCategories);
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleRemoveService = async (
    categoryId: string,
    serviceId?: string,
    priceOptionId?: string
  ) => {
    const targetCategory = categories.find(category => category.id === categoryId);
    if (!targetCategory) return;

    const updatedServices = (targetCategory.services || []).filter(item => {
      const matchesService = item.service?.id === serviceId;
      const itemPriceId = item.priceOption?.id || item.priceOption?.price_option_id;
      const matchesPriceOption = priceOptionId ? itemPriceId === priceOptionId : true;
      return !(matchesService && matchesPriceOption);
    });

    const previousCategories = categories;
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId ? { ...category, services: updatedServices } : category
      )
    );

    try {
      const { error } = await supabase
        .from('que_intake_flow')
        .update({ services: updatedServices })
        .eq('id', categoryId);

      if (error) throw error;
      toast.success('Service removed');
    } catch (error) {
      console.error('Error removing service:', error);
      toast.error('Failed to remove service');
      setCategories(previousCategories);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Que Builder</h1>
          <p className="text-sm text-gray-500">
            Configure how customers join and move through your queue.
          </p>
        </div>
      </div>

      {selectedBusiness?.slug && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => {
                  const queUrl = `${getSiteUrl()}/${selectedBusiness.slug}/que`;
                  window.open(queUrl, '_blank');
                }}
                className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 active:bg-blue-200 transition-all duration-150"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={() => {
                  const queUrl = `${getSiteUrl()}/${selectedBusiness.slug}/que`;
                  navigator.clipboard.writeText(queUrl);
                  toast.success('Que URL copied to clipboard!');
                }}
                className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 active:bg-blue-200 transition-all duration-150"
              >
                Copy Link
              </button>
            </div>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer list-none py-1 rounded-md px-1.5 -mx-1.5 transition-colors [&::-webkit-details-marker]:hidden [&::marker]:hidden">
                <span className="text-xs text-gray-600 font-medium">Que URL</span>
                <ChevronDown className="h-3 w-3 text-gray-400 transition-transform group-open:rotate-180 ml-2" />
              </summary>
              <div className="mt-2">
                <div className="bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
                  <code className="text-blue-600 text-xs break-all font-medium">
                    {getSiteUrl()}/{selectedBusiness.slug}/que
                  </code>
                </div>
              </div>
            </details>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Que builder tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'intake' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900">Intake Setup</h2>
            <p className="mt-2 text-sm text-gray-600">
              Define how customers enter the queue. This will power the public intake page.
            </p>

            <div className="mt-5 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Categories</h3>
                  <p className="text-xs text-gray-500">Step 1: Group services for intake.</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">
                    {categorySelectionEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <Switch
                    checked={categorySelectionEnabled}
                    onChange={handleToggleCategorySelection}
                    disabled={isLoadingCategorySetting}
                    className={`${
                      categorySelectionEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed`}
                  >
                    <span
                      className={`${
                        categorySelectionEnabled ? 'translate-x-5' : 'translate-x-1'
                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                {isLoadingCategories && (
                  <div className="text-sm text-gray-500">Loading categories...</div>
                )}

                {!isLoadingCategories && categories.length === 0 && (
                  <div className="text-sm text-gray-500">No categories yet.</div>
                )}

                {categories.map(category => (
                  <div
                    key={category.id}
                    className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                  >
                    {editingCategoryId === category.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingCategoryTitle}
                          onChange={(e) => setEditingCategoryTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateCategory(category.id);
                            } else if (e.key === 'Escape') {
                              handleCancelEditCategory();
                            }
                          }}
                          className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          autoFocus
                          disabled={isUpdatingCategory}
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateCategory(category.id)}
                          disabled={isUpdatingCategory || !editingCategoryTitle.trim()}
                          className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Save category title"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditCategory}
                          disabled={isUpdatingCategory}
                          className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Cancel editing"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span>{category.category_title}</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleStartEditCategory(category.id, category.category_title)}
                            className="text-gray-400 hover:text-blue-500 transition-colors"
                            aria-label={`Edit ${category.category_title}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCategory(category.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            aria-label={`Delete ${category.category_title}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={categoryTitle}
                  onChange={(event) => setCategoryTitle(event.target.value)}
                  placeholder="New category name"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <button
                  type="button"
                  onClick={handleCreateCategory}
                  disabled={isSavingCategory || !categoryTitle.trim()}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {isSavingCategory ? 'Saving...' : 'Add Category'}
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 text-sm text-gray-700">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Services</h2>
                    <p className="mt-1 text-xs text-gray-600">
                      Assign services to each category.
                    </p>
                  </div>
                </div>

                {!categorySelectionEnabled ? (
                  <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
                    Enable Categories to assign services by category.
                  </div>
                ) : (
                  <div className="mt-3 space-y-3">
                    {isLoadingCategories && (
                      <div className="text-xs text-gray-500">Loading categories...</div>
                    )}

                    {!isLoadingCategories && categories.length === 0 && (
                      <div className="text-xs text-gray-500">Create a category first.</div>
                    )}

                    {categories.map(category => (
                      <div key={category.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-semibold text-gray-900">
                            {category.category_title}
                          </h3>
                        </div>

                        <div className="mt-2 space-y-2">
                          {(category.services || []).length === 0 && (
                            <div className="text-xs text-gray-500">No services assigned.</div>
                          )}
                          {(category.services || []).map((item, index) => {
                            const secondaryServices = item.secondary_services || [];
                            const isExpanded = selectedServiceForSecondary?.categoryId === category.id &&
                              selectedServiceForSecondary?.serviceId === item.service?.id &&
                              selectedServiceForSecondary?.priceOptionId === (item.priceOption?.id || item.priceOption?.price_option_id);
                            
                            return (
                              <div
                                key={`${item.service?.id}-${item.priceOption?.id || index}`}
                                className="rounded-md border border-gray-200 bg-white"
                              >
                                <div className="flex items-center justify-between px-3 py-2 text-xs text-gray-700">
                                  <div className="flex-1">
                                    <span className="font-medium">{item.service?.name || 'Service'}</span>
                                    {item.priceOption?.name && (
                                      <div className="text-xs text-gray-500">{item.priceOption.name}</div>
                                    )}
                                    {secondaryServices.length > 0 && (
                                      <div className="text-xs text-blue-600 mt-1">
                                        {secondaryServices.length} add-on{secondaryServices.length !== 1 ? 's' : ''}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (isExpanded) {
                                          setSelectedServiceForSecondary(null);
                                        } else {
                                          setSelectedServiceForSecondary({
                                            categoryId: category.id,
                                            serviceId: item.service?.id,
                                            priceOptionId: item.priceOption?.id || item.priceOption?.price_option_id
                                          });
                                        }
                                      }}
                                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      {isExpanded ? 'Hide' : 'Add-ons'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveService(
                                        category.id,
                                        item.service?.id,
                                        item.priceOption?.id || item.priceOption?.price_option_id
                                      )}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                      aria-label="Remove service"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                                
                                {isExpanded && (
                                  <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                                    <div className="text-xs font-semibold text-gray-700 mb-2">Add-on Services</div>
                                    <div className="space-y-2">
                                      {secondaryServices.length === 0 && (
                                        <div className="text-xs text-gray-500">No add-ons assigned.</div>
                                      )}
                                      {secondaryServices.map((secondary: any, secIndex: number) => (
                                        <div
                                          key={`${secondary.service?.id}-${secondary.priceOption?.id || secIndex}`}
                                          className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1.5 text-xs"
                                        >
                                          <div>
                                            <span className="text-gray-700">{secondary.service?.name || 'Service'}</span>
                                            {secondary.priceOption?.name && (
                                              <div className="text-xs text-gray-500">{secondary.priceOption.name}</div>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              const updatedSecondary = secondaryServices.filter((s: any) => 
                                                !(s.service?.id === secondary.service?.id && 
                                                  (s.priceOption?.id || s.priceOption?.price_option_id) === 
                                                  (secondary.priceOption?.id || secondary.priceOption?.price_option_id))
                                              );
                                              
                                              const updatedServices = (category.services || []).map((s: any) => {
                                                if (s.service?.id === item.service?.id && 
                                                    (s.priceOption?.id || s.priceOption?.price_option_id) === 
                                                    (item.priceOption?.id || item.priceOption?.price_option_id)) {
                                                  return { ...s, secondary_services: updatedSecondary };
                                                }
                                                return s;
                                              });

                                              const { error } = await supabase
                                                .from('que_intake_flow')
                                                .update({ services: updatedServices })
                                                .eq('id', category.id);

                                              if (error) {
                                                toast.error('Failed to remove add-on');
                                                return;
                                              }

                                              setCategories(prev =>
                                                prev.map(c =>
                                                  c.id === category.id ? { ...c, services: updatedServices } : c
                                                )
                                              );
                                              toast.success('Add-on removed');
                                            }}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedCategoryForServices(category.id);
                                          setIsSelectServiceModalOpen(true);
                                        }}
                                        className="w-full inline-flex items-center justify-center rounded border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                                      >
                                        <Plus className="mr-1 h-3 w-3" />
                                        Add Add-on Service
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => openServiceModal(category.id)}
                            className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Add Service
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                Intake Form Details (coming soon).
              </div>
              {isStripeConnected && (
                <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Enable Stripe Pay</div>
                      <div className="text-xs text-gray-500">
                        Allow customers to pay during queue intake.
                      </div>
                    </div>
                    <Switch
                      checked={stripePayEnabled}
                      onChange={handleToggleStripePay}
                      disabled={isLoadingStripeSetting}
                      className={`${
                        stripePayEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed`}
                    >
                      <span
                        className={`${
                          stripePayEnabled ? 'translate-x-5' : 'translate-x-1'
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                </div>
              )}
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                Configure confirmation messaging and intake rules.
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">Queue Engine</h2>
          <p className="mt-2 text-sm text-gray-600">
            This will control how queue positions are assigned and updated.
          </p>
          <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
            Configure prioritization, capacity, and live position updates.
          </div>
        </div>
      )}

      {activeTab === 'handoff' && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Handoff Settings</h2>
              <p className="mt-1 text-sm text-gray-600">
                Define stages and capacity for queue handoff.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">Advance to Handoff</div>
                <div className="text-xs text-gray-500">
                  Automatically move bookings into handoff stages when capacity is available.
                </div>
              </div>
              <Switch
                checked={advanceToHandoff}
                onChange={handleToggleAdvanceToHandoff}
                disabled={isLoadingHandoffSetting}
                className={`${
                  advanceToHandoff ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed`}
              >
                <span
                  className={`${
                    advanceToHandoff ? 'translate-x-5' : 'translate-x-1'
                  } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
            </div>
          </div>

          {advanceToHandoff && (
            <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">Booking Status at Handoff</div>
                <div className="text-xs text-gray-500 mt-1">
                  Automatically update booking status when entering handoff stages.
                </div>
              </div>
              <div className="mt-3">
                <select
                  value={handoffBookingStatus || ''}
                  onChange={(e) => handleChangeHandoffBookingStatus(e.target.value as any || null)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">No status change</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          )}

          {advanceToHandoff && (
            <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">Handoff Completed</div>
                <div className="text-xs text-gray-500 mt-1">
                  Action to take when booking completes the final handoff stage.
                </div>
              </div>
              <div className="mt-3">
                <select
                  value={handoffCompletedAction || ''}
                  onChange={(e) => handleChangeHandoffCompletedAction(e.target.value as any || null)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">No action</option>
                  <option value="create_booking">Create Booking</option>
                </select>
              </div>
            </div>
          )}

          {advanceToHandoff && handoffCompletedAction === 'create_booking' && (
            <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Hide Completed Bookings from Calendar</div>
                  <div className="text-xs text-gray-500 mt-1">
                    When enabled, bookings completed from the queue will not appear in the calendar views.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleHideCompletedFromCalendar(!hideCompletedFromCalendar)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    hideCompletedFromCalendar ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      hideCompletedFromCalendar ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">Stages</div>
                <div className="text-xs text-gray-500">
                  Set the stage order and max bookings allowed in each stage.
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {isLoadingHandoffStages && (
                <div className="text-xs text-gray-500">Loading stages...</div>
              )}

              {!isLoadingHandoffStages && handoffStages.length === 0 && (
                <div className="text-xs text-gray-500">No stages created yet.</div>
              )}

              {handoffStages.map(stage => (
                <div
                  key={stage.id}
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                >
                  {editingStageId === stage.id ? (
                    <>
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={editingStageTitle}
                          onChange={(e) => setEditingStageTitle(e.target.value)}
                          className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1"
                          placeholder="Stage name"
                        />
                        <input
                          type="number"
                          min={1}
                          value={editingStageMaxBookings}
                          onChange={(e) => setEditingStageMaxBookings(Number(e.target.value))}
                          className="w-20 text-sm border border-gray-300 rounded-md px-2 py-1"
                          placeholder="Max"
                        />
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          type="button"
                          onClick={handleUpdateStage}
                          disabled={isUpdatingStage || !editingStageTitle.trim() || editingStageMaxBookings < 1}
                          className="text-green-600 hover:text-green-700 disabled:opacity-50"
                          title="Save"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditStage}
                          className="text-gray-400 hover:text-gray-600"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {stage.stage_order}. {stage.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          Max bookings: {stage.max_bookings} • {stage.handoff_mode === 'automatic' ? 'Auto handoff' : 'Manual handoff'}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <select
                          value={stage.handoff_mode}
                          onChange={(event) =>
                            handleUpdateHandoffMode(stage.id, event.target.value as 'automatic' | 'manual')
                          }
                          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-700 bg-white"
                        >
                          <option value="automatic">Auto handoff</option>
                          <option value="manual">Manual handoff</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => handleStartEditStage(stage)}
                          className="text-gray-400 hover:text-blue-500 transition-colors"
                          title="Edit stage"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteHandoffStage(stage.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label={`Delete ${stage.title}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Stage name</label>
                <input
                  type="text"
                  value={handoffTitle}
                  onChange={(event) => setHandoffTitle(event.target.value)}
                  placeholder="Wash, Dry, Detail"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Stage order</label>
                <input
                  type="number"
                  min={1}
                  value={handoffOrder}
                  onChange={(event) => setHandoffOrder(Number(event.target.value))}
                  placeholder="1"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <p className="text-[11px] text-gray-500">1 = first, 2 = next</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Max bookings</label>
                <input
                  type="number"
                  min={1}
                  value={handoffMaxBookings}
                  onChange={(event) => setHandoffMaxBookings(Number(event.target.value))}
                  placeholder="1"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <p className="text-[11px] text-gray-500">Capacity for this stage</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Handoff mode</label>
                <select
                  value={handoffMode}
                  onChange={(event) => setHandoffMode(event.target.value as 'automatic' | 'manual')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="automatic">Auto handoff</option>
                  <option value="manual">Manual handoff</option>
                </select>
                <p className="text-[11px] text-gray-500">Moves automatically or waits for manual completion</p>
              </div>
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={handleCreateHandoffStage}
                disabled={isSavingHandoffStage || !handoffTitle.trim()}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                {isSavingHandoffStage ? 'Saving...' : 'Add Stage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'design' && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">Design</h2>
          <p className="mt-2 text-sm text-gray-600">
            Customize the public queue experience.
          </p>

          <div className="mt-6 space-y-6">
            {/* Category Selection Label */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <label htmlFor="categoryLabel" className="block text-sm font-medium text-gray-700 mb-2">
                Category Selection Title
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Customize the title shown when customers select a category
              </p>
              <input
                id="categoryLabel"
                type="text"
                value={categorySelectionLabel}
                onChange={(e) => setCategorySelectionLabel(e.target.value)}
                onBlur={async () => {
                  if (!businessId) return;
                  try {
                    const { error } = await supabase
                      .from('que_settings')
                      .upsert({
                        business_id: businessId,
                        category_selection_label: categorySelectionLabel
                      });
                    if (error) throw error;
                    toast.success('Category title updated');
                  } catch (error) {
                    console.error('Error updating category title:', error);
                    toast.error('Failed to update category title');
                  }
                }}
                placeholder="Select Category"
                className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Thank You Page Text Settings */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Thank You Page</h3>
              <p className="text-sm text-gray-500 mb-4">
                Customize the text shown after a customer joins the queue
              </p>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="thankYouTitle" className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    id="thankYouTitle"
                    type="text"
                    value={thankYouTitle}
                    onChange={(e) => setThankYouTitle(e.target.value)}
                    onBlur={async () => {
                      if (!businessId) return;
                      try {
                        const { error } = await supabase
                          .from('que_settings')
                          .upsert({
                            business_id: businessId,
                            thank_you_title: thankYouTitle
                          });
                        if (error) throw error;
                        toast.success('Thank you title updated');
                      } catch (error) {
                        console.error('Error updating thank you title:', error);
                        toast.error('Failed to update thank you title');
                      }
                    }}
                    placeholder="You're in the Queue!"
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="thankYouMessage" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="thankYouMessage"
                    value={thankYouMessage}
                    onChange={(e) => setThankYouMessage(e.target.value)}
                    onBlur={async () => {
                      if (!businessId) return;
                      try {
                        const { error } = await supabase
                          .from('que_settings')
                          .upsert({
                            business_id: businessId,
                            thank_you_message: thankYouMessage
                          });
                        if (error) throw error;
                        toast.success('Thank you message updated');
                      } catch (error) {
                        console.error('Error updating thank you message:', error);
                        toast.error('Failed to update thank you message');
                      }
                    }}
                    placeholder="We've added you to the queue and will get to you as soon as possible."
                    rows={3}
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Header Settings */}
            <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Header</div>
                  <div className="text-xs text-gray-500">
                    Show a top header on the public queue page.
                  </div>
                </div>
              <Switch
                checked={headerEnabled}
                onChange={handleToggleHeader}
                className={`${
                  headerEnabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    headerEnabled ? 'translate-x-5' : 'translate-x-1'
                  } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>

              {headerEnabled && (
                <div className="mt-4 flex items-center gap-3">
                  <label className="text-xs font-semibold text-gray-600">Header color</label>
                  <input
                    type="color"
                    value={headerColor}
                    onChange={(event) => handleHeaderColorChange(event.target.value)}
                    className="h-9 w-14 rounded-md border border-gray-200 bg-white"
                  />
                  <span className="text-xs text-gray-500">{headerColor.toUpperCase()}</span>
                </div>
              )}

              {headerEnabled && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                  <div>
                    <div className="text-xs font-semibold text-gray-900">Display logo</div>
                    <div className="text-[11px] text-gray-500">Use the business logo.</div>
                  </div>
                  <Switch
                    checked={displayLogo}
                    onChange={handleToggleDisplayLogo}
                    className={`${
                      displayLogo ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        displayLogo ? 'translate-x-5' : 'translate-x-1'
                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                  <div>
                    <div className="text-xs font-semibold text-gray-900">Display company name</div>
                    <div className="text-[11px] text-gray-500">Show the business name.</div>
                  </div>
                  <Switch
                    checked={displayCompanyName}
                    onChange={handleToggleDisplayCompanyName}
                    className={`${
                      displayCompanyName ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        displayCompanyName ? 'translate-x-5' : 'translate-x-1'
                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => {
          setIsSelectServiceModalOpen(false);
          setSelectedCategoryForServices(null);
        }}
        onAdd={handleAssignService}
        closeOnAdd={false}
      />
    </div>
  );
}

